import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import ToggleTabGroup from '../../components/atoms/ToggleTabGroup';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import Tooltip from '../../components/molecules/Tooltip';
import { Label } from '../../components/widgets';
import { DEFAULT_SECTION_ACCESS_TYPE, SECTION_ACCESS_TYPES } from '../../utils/consts';
import { translationPrefix as translationRootPrefix } from '../utils';
import css from './SectionsStepCard.css';
import StepCard from './StepCard';

const translationPrefix = `${translationRootPrefix}.MANAGE_PERMISSIONS_PAGE`;

const ICON_NAMES_BY_ACCESS_TYPE = {
    [SECTION_ACCESS_TYPES.VIEW]: 'eye',
    [SECTION_ACCESS_TYPES.EDIT]: 'check',
};

const ICON_VARIANTS_BY_ACCESS_TYPE = {
    [SECTION_ACCESS_TYPES.VIEW]: IconVariants.ACTIVE,
    [SECTION_ACCESS_TYPES.EDIT]: IconVariants.SUCCESS,
};

const SectionsStepCard = ({
    title,
    description,
    sections,
    sectionPermissions,
    onToggleAccessType,
    parts,
    step,
    translate,
}) => {
    const accessTypes = Object.values(SECTION_ACCESS_TYPES)
        .sort((a, b) => b - a)
        .map(accessType => ({
            label: translate(`${translationPrefix}.SECTION_ACCESS_TYPES.${accessType}`),
            option: accessType,
        }));

    // For now, all sections have the same access type, so we can use the first section's access type
    const selectedAccessType = useMemo(() => {
        if (sectionPermissions?.length > 0) {
            return sectionPermissions[0].access_type;
        }
        return DEFAULT_SECTION_ACCESS_TYPE;
    }, [sectionPermissions]);

    const accessTypesRecord = sections.reduce((acc, section) => {
        const sectionPermission = sectionPermissions?.find(({ name }) => name === section.name);
        acc[section.name] = sectionPermission ? sectionPermission.access_type : DEFAULT_SECTION_ACCESS_TYPE;
        return acc;
    }, {});

    const viewOnlySections = useMemo(() => parts.filter(part => part.viewOnly).flatMap(part => part.sectionNames), [
        parts,
    ]);

    useEffect(() => {
        onToggleAccessType(viewOnlySections, SECTION_ACCESS_TYPES.VIEW);
    }, [viewOnlySections, onToggleAccessType]);

    const handleOnToggle = newAccessType => {
        onToggleAccessType(
            sections.map(section => section.name).filter(sectionName => !viewOnlySections.includes(sectionName)),
            newAccessType
        );
    };

    const getAccessType = sectionNames => {
        const hasFullAccess = sectionNames.every(
            sectionName => accessTypesRecord[sectionName] === SECTION_ACCESS_TYPES.EDIT
        );
        return hasFullAccess ? SECTION_ACCESS_TYPES.EDIT : SECTION_ACCESS_TYPES.VIEW;
    };

    const renderPart = part => {
        let displayName = '';
        if (part.displayName) displayName += `${translate(part.displayName)} - `;
        part.sectionNames.forEach((sectionName, index) => {
            displayName += `${index > 0 ? ' & ' : ''} ${translate(
                `${translationPrefix}.SECTIONS.${sectionName}.DISPLAY_NAME`
            )}`;
        });
        const accessType = getAccessType(part.sectionNames);
        return (
            <div className={css.part} key={displayName}>
                <div className={css.accessTypeContainer}>
                    <Icon
                        name={ICON_NAMES_BY_ACCESS_TYPE[accessType]}
                        size={IconSizes.SMALL}
                        variant={ICON_VARIANTS_BY_ACCESS_TYPE[accessType]}
                    />
                    <Translate id={`${translationPrefix}.SECTION_ACCESS_TYPES.${accessType}`} />
                </div>
                <span className={css.sectionName}>{displayName}</span>
                {(part.tooltipImageSrc || part.tooltipDescriptionSectionName) && (
                    <Tooltip
                        title={
                            <>
                                {part.tooltipDescriptionSectionName &&
                                    translate(
                                        `${translationPrefix}.SECTIONS.${part.tooltipDescriptionSectionName}.TOOLTIP_DESCRIPTION`
                                    )}
                                {part.tooltipImageSrc && (
                                    <img src={part.tooltipImageSrc} alt="" className={css.tooltipImage} />
                                )}
                            </>
                        }
                    >
                        <Icon name="question-line" size={IconSizes.X_SMALL} variant={IconVariants.LIGHT} />
                    </Tooltip>
                )}
            </div>
        );
    };

    return (
        <StepCard
            title={title}
            description={description}
            headerChildren={
                <ToggleTabGroup options={accessTypes} selectedOption={selectedAccessType} onToggle={handleOnToggle} />
            }
        >
            <Label
                text={`${translationPrefix}.${step.toUpperCase()}.SECTION_ACCESS_TYPES_DESCRIPTION.${selectedAccessType}`}
                type="sideLabel"
            />
            <div className={css.sectionsContainer}>{parts.map(renderPart)}</div>
        </StepCard>
    );
};

SectionsStepCard.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    sectionPermissions: PropTypes.arrayOf(
        PropTypes.shape({
            access_type: PropTypes.number.isRequired,
            display_name: PropTypes.string.isRequired,
            feature_name: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            ui_key: PropTypes.string.isRequired,
        })
    ).isRequired,
    sections: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            display_name: PropTypes.string.isRequired,
            feature_name: PropTypes.string.isRequired,
        })
    ).isRequired,
    parts: PropTypes.arrayOf(
        PropTypes.shape({
            sectionNames: PropTypes.arrayOf(PropTypes.string).isRequired,
            displayName: PropTypes.string,
            tooltipImageSrc: PropTypes.string,
            tooltipDescriptionSectionName: PropTypes.string,
            viewOnly: PropTypes.bool,
        })
    ).isRequired,
    onToggleAccessType: PropTypes.func.isRequired,
    step: PropTypes.string.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(SectionsStepCard);
